import { Controller } from "@hotwired/stimulus"

const LIMIT = 8

// Autocomplétion de l'entreprise et de l'interlocuteur sur l'écran de création
// d'un audit. L'annuaire est embarqué dans la page (voir Crm::Company.directory
// pour le pourquoi) : le filtrage est donc instantané et ne dépend pas du
// réseau, ce qui compte en salle de réunion.
//
// Les deux champs partagent le même code, distingués par un paramètre d'action
// `field` : ce sont deux instances d'un même comportement, pas deux
// comportements voisins.
//
// Un nom absent de l'annuaire reste valide dans les deux champs : le serveur
// crée la société ou le contact. La liste suggère, elle ne contraint pas.
export default class extends Controller {
  static targets = ["companyInput", "companyList", "contactInput", "contactList", "emailInput"]
  static values = { directory: Array }

  connect() {
    this.userActive = false
    this.markActive = () => { this.userActive = true }
    // En capture : la touche Tab ou le clic doivent être enregistrés avant le
    // focus qu'ils provoquent, sinon ce premier focus serait ignoré à tort.
    for (const type of [ "pointerdown", "keydown" ]) {
      document.addEventListener(type, this.markActive, { capture: true, once: true })
    }
  }

  disconnect() {
    for (const type of [ "pointerdown", "keydown" ]) {
      document.removeEventListener(type, this.markActive, { capture: true })
    }
  }

  filter(event) {
    this.show(event.params.field)
  }

  // Le champ Entreprise porte `autofocus` : ouvrir au focus sans condition
  // dépliait la liste dès le chargement de la page, par-dessus les champs
  // suivants et leurs libellés, avant la moindre action. La liste n'ouvre donc
  // au focus qu'une fois que la personne a touché quelque chose -- ce qui
  // laisse intact le clic sur un champ vide, seule façon de voir les contacts
  // d'une société sans rien taper. Le clic déclenche la même méthode : le
  // champ autofocalisé est déjà focalisé, le cliquer n'émet donc aucun `focus`.
  open(event) {
    if (this.userActive) this.show(event.params.field)
  }

  navigate(event) {
    const field = event.params.field

    if (event.key === "Escape") {
      this.hide(field)
    } else if (this.options(field).length === 0) {
      // Rien à parcourir : Entrée doit soumettre le formulaire comme d'habitude.
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      this.move(field, event.key === "ArrowDown" ? 1 : -1)
    } else if (event.key === "Enter" && this.active(field)) {
      event.preventDefault()
      this.pick(field, this.active(field))
    }
  }

  // `pointerdown` et non `click` : le pointeur enfoncé sur une option retire le
  // focus du champ, ce qui refermerait la liste avant que le clic n'aboutisse.
  // Le preventDefault garde le focus là où il est.
  choose(event) {
    event.preventDefault()
    this.pick(event.params.field, event.currentTarget)
  }

  close(event) {
    this.hide(event.params.field)
  }

  show(field) {
    const input = this.input(field)
    const list = this.list(field)
    const term = input.value.trim().toLowerCase()
    // `includes` et non `startsWith` : « menuiserie » doit trouver
    // « Ets Menuiserie Nouvelle », ce que la complétion native ne fait pas.
    const items = this.source(field).filter((item) => item.name.toLowerCase().includes(term)).slice(0, LIMIT)

    list.replaceChildren(...items.map((item) => this.option(field, item)))
    list.hidden = items.length === 0
    input.setAttribute("aria-expanded", String(!list.hidden))
    this.setActive(field, -1)
  }

  hide(field) {
    this.list(field).hidden = true
    this.input(field).setAttribute("aria-expanded", "false")
    this.input(field).removeAttribute("aria-activedescendant")
  }

  pick(field, option) {
    const input = this.input(field)
    const changed = input.value !== option.dataset.value

    input.value = option.dataset.value
    this.hide(field)

    if (field === "company") {
      // Changer de société invalide l'interlocuteur : les contacts affichés ne
      // sont plus les siens.
      if (changed) this.resetContact()
      this.contactInputTarget.focus()
    } else {
      this.emailInputTarget.value = option.dataset.email || ""
    }
  }

  resetContact() {
    this.contactInputTarget.value = ""
    this.emailInputTarget.value = ""
  }

  // Les contacts proposés sont ceux de la société saisie, et d'elle seule. Une
  // société inconnue de l'annuaire n'en a aucun : c'est une création.
  source(field) {
    if (field === "company") return this.directoryValue

    const name = this.companyInputTarget.value.trim().toLowerCase()
    const company = this.directoryValue.find((entry) => entry.name.toLowerCase() === name)

    return company ? company.contacts : []
  }

  // Construit l'option en DOM plutôt qu'en innerHTML : les noms viennent du CRM
  // et ne doivent jamais être interprétés comme du balisage.
  option(field, item) {
    const option = document.createElement("li")

    option.className = "capture-option"
    option.setAttribute("role", "option")
    option.setAttribute("aria-selected", "false")
    option.dataset.action = "pointerdown->capture-directory#choose"
    option.dataset.captureDirectoryFieldParam = field
    option.dataset.value = item.name
    if (item.email) option.dataset.email = item.email

    const name = document.createElement("span")
    name.textContent = item.name
    option.appendChild(name)

    const hint = this.hint(field, item)
    if (hint) {
      const note = document.createElement("span")
      note.className = "capture-option-hint"
      note.textContent = hint
      option.appendChild(note)
    }

    return option
  }

  hint(field, item) {
    if (field === "contact") return item.email || ""

    const count = item.contacts.length
    return count === 0 ? "Aucun contact" : `${count} contact${count > 1 ? "s" : ""}`
  }

  move(field, delta) {
    const options = this.options(field)
    const current = options.indexOf(this.active(field))
    const next = current === -1 ? (delta > 0 ? 0 : options.length - 1) : (current + delta + options.length) % options.length

    this.setActive(field, next)
  }

  setActive(field, index) {
    const options = this.options(field)

    options.forEach((option, position) => {
      const active = position === index

      option.classList.toggle("capture-option-active", active)
      option.setAttribute("aria-selected", String(active))
      if (active) {
        option.id = `${field}-option-${position}`
        option.scrollIntoView({ block: "nearest" })
      }
    })

    const active = options[index]
    if (active) this.input(field).setAttribute("aria-activedescendant", active.id)
    else this.input(field).removeAttribute("aria-activedescendant")
  }

  active(field) {
    return this.list(field).querySelector(".capture-option-active")
  }

  options(field) {
    return Array.from(this.list(field).querySelectorAll("[role=option]"))
  }

  input(field) {
    return field === "company" ? this.companyInputTarget : this.contactInputTarget
  }

  list(field) {
    return field === "company" ? this.companyListTarget : this.contactListTarget
  }
}
